import React, { useCallback, useRef, useState } from 'react';

import { BiX } from 'react-icons/bi';

import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import UnformInput from '../../../../newComponents/UnformInput';
import Button from '../../../../newComponents/Button';

import * as S from './styles';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';

import api from '../../../../services/api';

import getValidationErrors from '../../../../utils/getValidationErrors';
import { maskCpf, maskPhone } from '../../../../utils/mask';
import { ICustomer } from '../../../../interfaces/ICustomer';

interface CreateUserModalProps {
  modalRef: any;
  setCustomers: React.Dispatch<React.SetStateAction<ICustomer[]>>;
  setSelectedClient: React.Dispatch<React.SetStateAction<ICustomer | null>>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = (props) => {
  const { modalRef, setCustomers, setSelectedClient } = props;

  const formRefCreateClient = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<{
    name: string | undefined;
    surname: string | undefined;
    phone: string | undefined;
    email: string | undefined;
    cnpjcpf: string | undefined;
  }>({
    name: undefined,
    surname: undefined,
    phone: undefined,
    email: undefined,
    cnpjcpf: undefined,
  });

  const { addToast } = useToast();
  const { user } = useAuth();

  const handleCreateClient = useCallback(async () => {
    setIsLoading(true);

    try {
      formRefCreateClient.current?.setErrors({});

      const schema = Yup.object().shape({
        name: Yup.string()
          .required('Infome o nome do cliente para continuar')
          .min(3, 'Mínimo de 3 caracteres'),
        surname: Yup.string()
          .required('Infome o sobrenome do cliente para continuar')
          .min(3, 'Mínimo de 3 caracteres'),
        email: Yup.string().notRequired().email('Digite um e-mail válido'),
        phone: Yup.string()
          .required('Informe um telefone para continuar.')
          .min(14, 'Mínimo de 10 números')
          .max(15, 'Máximo de 11 números'),
        cnpjcpf: Yup.string()
          .notRequired()
          .min(14, 'Mínimo de 11 números')
          .max(14, 'Máximo de 11 números'),
      });

      await schema.validate(userData, { abortEarly: false });
      const { name, cnpjcpf, email, surname, phone } = userData;

      const { data } = await api.post(
        `companies/${user.empresa_id}/customers`,
        {
          companyId: user.empresa_id,
          tradingName: `${name} ${surname}`,
          address: {},
          email,
          CNPJCPF: cnpjcpf?.replace(/\D/g, ''),
          phoneNumber: phone?.replace(/\D/g, ''),
        },
      );

      addToast({
        type: 'success',
        title: 'Cliente Criado',
      });

      setCustomers((oldState) => [...oldState, data]);
      setSelectedClient(data);
      setIsLoading(false);

      modalRef.current?.close();
    } catch (err) {
      console.log(err);
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRefCreateClient.current?.setErrors(errors);
      } else if (
        (err as any).response.status === 409 &&
        (err as any)?.response.data.message === 'E-mail já em uso'
      ) {
        formRefCreateClient.current?.setErrors({
          email: 'E-mail já em uso',
        });
      } else if (
        (err as any).response.status === 409 &&
        (err as any)?.response.data.message === 'Telefone já em uso'
      ) {
        formRefCreateClient.current?.setErrors({
          phone: 'Telefone já em uso',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro no servidor.',
        });
      }

      setIsLoading(false);
    }
  }, [addToast, modalRef, user.empresa_id, userData]);

  const handlePhone = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setUserData((oldState) => ({
      ...oldState,
      phone: value ? maskPhone(value) : undefined,
    }));
  };

  const handleCPF = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setUserData((oldState) => ({
      ...oldState,
      cnpjcpf: value ? maskCpf(value) : undefined,
    }));
  };

  return (
    <S.CustomModal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
      <S.ModalHeader>
        <h1>Cadastro de clientes</h1>
        <button type="button" onClick={() => modalRef.current?.close()}>
          <BiX />
        </button>
      </S.ModalHeader>
      <S.ModalBody>
        <Form
          ref={formRefCreateClient}
          onSubmit={handleCreateClient}
          initialData={userData}
        >
          <UnformInput
            name="name"
            label="Nome *"
            placeholder="Ex.: Rodrigo"
            disabled={isLoading}
            value={userData.name}
            onChange={(e) => {
              const { value } = e.target;

              setUserData((oldState) => ({
                ...oldState,
                name: value || undefined,
              }));
            }}
          />
          <UnformInput
            name="surname"
            label="Sobrenome *"
            placeholder="Ex.: Silva"
            disabled={isLoading}
            value={userData.surname}
            onChange={(e) => {
              const { value } = e.target;

              setUserData((oldState) => ({
                ...oldState,
                surname: value || undefined,
              }));
            }}
          />
          <UnformInput
            name="phone"
            label="Telefone *"
            placeholder="Ex.: (XX) 9XXXX-XXXX"
            disabled={isLoading}
            maxLength={15}
            value={userData.phone}
            onChange={handlePhone}
          />
          <UnformInput
            name="email"
            label="E-mail"
            type="email"
            placeholder="Ex.: jonhdoe@mail.com"
            disabled={isLoading}
            value={userData.email}
            onChange={(e) => {
              const { value } = e.target;

              setUserData((oldState) => ({
                ...oldState,
                email: value || undefined,
              }));
            }}
          />
          <UnformInput
            name="cnpjcpf"
            label="CPF"
            placeholder="Ex.: XXX.XXX.XXX-XX "
            disabled={isLoading}
            maxLength={14}
            value={userData.cnpjcpf}
            onChange={handleCPF}
          />
          <Button disabled={isLoading} type="submit" full>
            Confirmar
          </Button>
        </Form>
      </S.ModalBody>
      <S.ModalFooter>
        <Button
          disabled={isLoading}
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </S.ModalFooter>
    </S.CustomModal>
  );
};

export default CreateUserModal;
